/*
 * terminal-device.js  (ES5 + jQuery 3)
 * Runs in the terminal app. Handles one transaction at a time.
 *
 * Lifecycle: request -> persist -> progress 'accepted' -> progress 'handoff' -> flush -> bridge.start()
 *   (app goes to background, socket may die)
 *   native result -> SocketHub.deliverNativeResult({status, data}) -> persist -> send
 *   resend on every reconnect / 5s until the server answers terminal.result_ack
 *
 * bridge: { start(req), print(html, segments), cancel(txId)?, recover(txId, cb)? }
 */
(function (window, $) {
  'use strict';
  var T = window.SocketHub,
    M = T.MSG;
  var STORE = 'terminal.device.inflight',
    GRACE_MS = 30000,
    RETRY_MS = 5000;
  var sock = null,
    bridge = null,
    isReady = function () {
      return true;
    };
  var cur = null; // {txId, posId, kind, startedAt, stage, result|null}
  var retryT = null,
    graceT = null,
    queue = [];
  var lastAnnounced = null;
  var recentJobs = {},
    recentOrder = [];

  function save() {
    try {
      if (cur) localStorage.setItem(STORE, JSON.stringify(cur));
      else localStorage.removeItem(STORE);
    } catch (e) {}
  }
  function load() {
    try {
      var c = JSON.parse(localStorage.getItem(STORE) || 'null');
      cur = c && typeof c.txId === 'string' ? c : null;
    } catch (e) {
      cur = null;
    }
  }

  function rememberJob(id, state, reason) {
    recentJobs[id] = { state: state, reason: reason };
    recentOrder.push(id);
    while (recentOrder.length > 50) delete recentJobs[recentOrder.shift()];
  }

  function printReply(m, state, reason) {
    sock.send(M.STATUS, {
      to: m.from,
      jobId: m.jobId || null,
      state: state,
      reason: reason || null,
    });
  }

  function handlePrint(m) {
    var dup = m.jobId && recentJobs[m.jobId];
    if (dup) {
      printReply(m, dup.state, dup.reason);
      return;
    }

    var state,
      reason = null;
    if (!bridge || typeof bridge.print !== 'function') {
      state = 'print_rejected';
      reason = 'unsupported';
    } else if (cur) {
      state = 'print_rejected';
      reason = 'busy';
    } else if (!isReady()) {
      state = 'print_rejected';
      reason = 'not_ready';
    } else if (
      (typeof m.document !== 'string' || !m.document) &&
      !($.isArray(m.segments) && m.segments.length)
    ) {
      state = 'print_rejected';
      reason = 'bad_request';
    } else {
      try {
        bridge.print(m.document || '', $.isArray(m.segments) ? m.segments : []);
        state = 'print_dispatched';
      } catch (e) {
        state = 'print_failed';
        reason = 'driver_error'; // same text key the UI already maps, no UI change needed
      }
    }
    if (m.jobId) rememberJob(m.jobId, state, reason);
    printReply(m, state, reason);
  }

  function reject(m, reason) {
    sock.send(M.PAYMENT_RESULT, {
      txId: m.txId,
      kind: m.kind,
      status: T.RESULT.FAILED,
      reason: reason,
    });
  }

  function sendResult() {
    clearTimeout(retryT);
    if (!cur || !cur.result) return;
    if (sock.isReady()) {
      sock.send(M.PAYMENT_RESULT, {
        txId: cur.txId,
        kind: cur.kind,
        status: cur.result.status,
        reason: cur.result.reason,
        data: cur.result.data,
      });
    }
    retryT = setTimeout(sendResult, RETRY_MS); // until result_ack
  }

  function complete(result) {
    if (!cur || cur.result) return;
    clearTimeout(graceT);
    cur.result = result;
    save(); // persist BEFORE sending: the result must survive a crash
    sendResult();
  }

  /** Native layer calls this when the payment application returns. */
  function deliver(res) {
    if (!cur) return false;
    if (res && res.txId && res.txId !== cur.txId) return false;
    if (cur.result) return true;
    var ok = false;
    $.each(T.RESULT, function (k, v) {
      if (res && res.status === v) ok = true;
    });
    complete({
      status: ok ? res.status : T.RESULT.FAILED,
      reason: (res && res.reason) || null,
      data: (res && res.data) || null,
    });
    return true;
  }

  // Foreground again but native has not reported: wait, ask native, else report UNKNOWN
  function armGrace() {
    clearTimeout(graceT);
    if (
      !cur ||
      cur.result ||
      cur.stage !== 'handoff' ||
      document.visibilityState !== 'visible'
    )
      return;
    graceT = setTimeout(function () {
      if (!cur || cur.result) return;
      var txId = cur.txId;
      var unknown = function () {
        complete({
          status: T.RESULT.UNKNOWN,
          reason: 'no_result_after_resume',
          data: null,
        });
      };
      if (bridge && typeof bridge.recover === 'function') {
        bridge.recover(txId, function (r) {
          if (r && r.status) deliver($.extend({ txId: txId }, r));
          else unknown();
        });
      } else {
        unknown();
      }
    }, GRACE_MS);
  }

  function computeReady() {
    return document.visibilityState === 'visible' && isReady() && !cur;
  }

  function announce(force) {
    if (!sock || !sock.isReady()) return;
    var r = computeReady();
    if (!force && r === lastAnnounced) return;
    if (sock.send(M.AVAILABILITY, { ready: r })) lastAnnounced = r;
  }

  T.refreshAvailability = function () {
    if (sock) announce();
  };

  function onRequest(m) {
    if (typeof m.txId !== 'string' || typeof m.from !== 'string') return;
    if (cur) {
      if (cur.txId === m.txId) {
        sock.send(M.PROGRESS, { txId: m.txId, stage: cur.stage });
        return;
      } // duplicate
      reject(m, 'terminal_busy');
      return;
    }
    if (!isReady()) {
      reject(m, 'not_ready');
      return;
    }
    if (m.kind !== 'payment' && m.kind !== 'refund') {
      reject(m, 'bad_request');
      return;
    }
    cur = {
      txId: m.txId,
      posId: m.from,
      kind: m.kind,
      startedAt: Date.now(),
      stage: 'accepted',
      result: null,
    };
    save();
    announce();

    if (typeof isTerminalBusy !== 'undefined') isTerminalBusy = true; // legacy global
    sock.send(M.PROGRESS, { txId: m.txId, stage: 'accepted' });

    cur.stage = 'handoff';
    save();
    sock.send(M.PROGRESS, { txId: m.txId, stage: 'handoff' }); // tells the POS: the next disconnect is expected
    sock.flush(function () {
      // be sure 'handoff' left the device before we are backgrounded
      try {
        var r = bridge.start({
          txId: m.txId,
          kind: m.kind,
          payload: m.payload,
        });
        if (r && r.status) deliver($.extend({ txId: m.txId }, r));
      } catch (e) {
        complete({
          status: T.RESULT.FAILED,
          reason: 'bridge_error',
          data: null,
        });
      }
    }, 800);
  }

  function onMessage(m) {
    switch (m.type) {
      case M.PAYMENT_REQUEST:
        onRequest(m);
        break;
      case M.CANCEL:
        if (!cur || cur.txId !== m.txId || cur.result) break;
        if (bridge && typeof bridge.cancel === 'function')
          bridge.cancel(m.txId);
        else sock.send(M.STATUS, { txId: m.txId, state: 'cancel_unsupported' });
        break; // the final outcome still arrives through deliver()
      case M.STATUS_REQUEST:
        // no txId on purpose: the reply goes to the asking POS, not to the tx owner
        sock.send(M.STATUS, {
          to: m.from,
          state: cur ? (cur.result ? 'result_pending' : 'busy') : 'idle',
          activeTx: cur ? cur.txId : null,
        });
        break;
      case M.PRINT:
        handlePrint(m);
        break;
      // if (cur) {
      //   sock.send(M.STATUS, {
      //     to: m.from,
      //     jobId: m.jobId || null,
      //     state: 'print_rejected',
      //     reason: 'busy',
      //   });
      //   break;
      // }
      // if (!isReady() || typeof m.document !== 'string' || !m.document) {
      //   sock.send(M.STATUS, {
      //     to: m.from,
      //     jobId: m.jobId || null,
      //     state: 'print_rejected',
      //     reason: 'not_ready',
      //   });
      //   break;
      // }
      // try {
      //   bridge.print(m.document);
      //   sock.send(M.STATUS, {
      //     to: m.from,
      //     jobId: m.jobId || null,
      //     state: 'print_dispatched',
      //   });
      // } catch (e) {
      //   sock.send(M.STATUS, {
      //     to: m.from,
      //     jobId: m.jobId || null,
      //     state: 'print_failed',
      //   });
      // }
      // break;
      case M.RESULT_ACK:
        if (cur && m.txId === cur.txId) {
          clearTimeout(retryT);
          clearTimeout(graceT);
          cur = null;
          save();
          announce();
          if (typeof isTerminalBusy !== 'undefined') isTerminalBusy = false;
        }
        break;
    }
  }

  function onReady(w) {
    lastAnnounced = null;
    announce(true);
    if (cur) {
      if (cur.result) {
        sendResult();
      } else {
        sock.send(M.PROGRESS, { txId: cur.txId, stage: cur.stage }); // still in the payment app
        armGrace();
      }
    } else if (w && w.activeTx) {
      // server thinks we are mid-payment but we lost our state: report UNKNOWN so the POS reconciles
      sock.send(M.PAYMENT_RESULT, {
        txId: w.activeTx,
        status: T.RESULT.UNKNOWN,
        reason: 'terminal_lost_state',
      });
    }
  }

  // native may answer before init ran: queue it
  T.deliverNativeResult = function (res) {
    if (!sock) {
      queue.push(res);
      return true;
    }
    return deliver(res);
  };

  /** opts: url, tokenUrl, serial, bridge, isReady() */
  T.initDevice = function (opts) {
    if (sock) return;
    bridge = opts.bridge;
    if (opts.isReady) isReady = opts.isReady;
    load();
    if (cur && typeof isTerminalBusy !== 'undefined') isTerminalBusy = true;
    sock = T.createSocket({
      url: opts.url,
      tokenUrl: opts.tokenUrl,
      role: 'terminal',
      deviceId: opts.serial,
      onReady: onReady,
      onMessage: onMessage,
    });
    $(document).on('visibilitychange', function () {
      if (document.visibilityState === 'visible') {
        armGrace();
      } else {
        clearTimeout(graceT);
      }
      announce();
    });
    $(window).on('pagehide', function () {
      if (sock && sock.isReady()) sock.send(M.AVAILABILITY, { ready: false });
    });
    setInterval(announce, 5000);
    while (queue.length) deliver(queue.shift());
    sock.start();
  };
})(window, jQuery);
